import React, { useEffect, useMemo, useState } from "react";

import {
  Button,
  Switch,
  TextField,
  Autocomplete,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";

import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { formatByText, onChangeMask } from "utils";
import {
  isValidProductPriceInput,
  parseMaskedCurrencyToNumber,
} from "utils/productPriceValidation";
import { toast } from "utils/toast";

import { CoverageAreaProps } from "store/modules/Catalog/types.store";
import {
  isGasDoPovoCatalogProduct,
  isGasDoPovoProductId,
} from "utils/gasDoPovo";
import {
  CoverageAreaType,
  isDealerStoreType,
  isDriverStoreType,
} from "types/coverageAreaType";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { handleUpdateCatalogAreaRequest } from "store/modules/Catalog/thunk.store";
import { useParams } from "react-router-dom";
import * as Styled from "./styles";

interface ICardProductCoveredProps {
  coverageArea: CoverageAreaProps;
  accordion?: boolean;
  dealerSelected: string;
  handleOpenArea?: (value: string) => void;
  gasDoPovo?: boolean;
  workingAreaType?: CoverageAreaType;
}

const CardProductCovered = ({
  coverageArea,
  accordion,
  handleOpenArea,
  dealerSelected,
  gasDoPovo = false,
  workingAreaType,
}: ICardProductCoveredProps) => {
  const { t } = useTranslation();
  const hideGasDoPovo =
    gasDoPovo === false ||
    isDealerStoreType(workingAreaType) ||
    isDriverStoreType(workingAreaType);

  const products = useMemo(() => {
    const productPrices = coverageArea?.productPrices ?? [];
    const mapped = productPrices.map((x) => ({
      description: x?.product?.description,
      name: x?.product?.name,
      enabled: x?.enabled,
      price: formatByText?.formatCurrency(x?.price),
      productId: x?.product?.id,
    }));

    return { products: mapped };
  }, [coverageArea]);

  const showZeroFieldToast = () => {
    toast.error(String(t("fieldCannotBeZero")));
  };

  const { register, control, reset, watch } = useForm({
    defaultValues: products,
    mode: "onChange",
  });

  const { catalogProducts } = useAppSelector((state) => state.catalog);

  const methods = useForm();

  const { dealerId = "" } = useParams<{ dealerId: string }>();

  const { register: registerAdd, setValue } = methods;

  const { fields, remove, append, replace } = useFieldArray({
    control,
    name: "products",
  });

  useEffect(() => {
    if (products?.products?.length === 0) {
      reset();
    }
    //  reset();
    replace(products.products);
  }, [coverageArea, replace, reset, products]);

  /* TODO => passar como prop */

  const [edit, setEdit] = useState(false);

  const [productSelected, setProductSelected] = useState("");
  const [productNewPrice, setProductNewPrice] = useState("");
  const [productNewId, setProductNewId] = useState("");

  const dispatch = useAppDispatch();

  const watchedProducts = watch("products");

  const IProducts = useMemo(() => {
    const currentProducts = watchedProducts ?? products.products ?? [];
    const cover = currentProducts.map((x) => ({
      label: formatByText.formatProductNameAndDescription(
        x?.name,
        x?.description,
      ),
      id: x?.productId,
    }));

    return catalogProducts?.data
      .filter((value) => !hideGasDoPovo || !isGasDoPovoCatalogProduct(value))
      ?.map((x) => ({
        label: formatByText.formatProductNameAndDescription(
          x?.name,
          x?.description,
        ),
        id: x?.id,
      }))
      ?.filter((x) => !cover?.find((y) => y.id === x.id));
  }, [
    catalogProducts?.data,
    hideGasDoPovo,
    products.products,
    watchedProducts,
  ]);

  const resetFormFields = () => {
    setProductSelected("");
    setProductNewId("");
    setProductNewPrice("");
  };

  const handleSaveProducts = async () => {
    const result = await dispatch(
      handleUpdateCatalogAreaRequest({
        data: watch("products").map((x) => ({
          ...x,
          price: parseMaskedCurrencyToNumber(x.price),
        })),
        userId: dealerId,
        id: dealerSelected,
      }),
    );

    if (handleUpdateCatalogAreaRequest.fulfilled.match(result)) {
      setEdit(false);
      resetFormFields();
    }
  };

  useEffect(() => {
    setEdit(false);
    resetFormFields();
  }, [coverageArea?.coverageAreaId]);

  return (
    <Styled.Container margin={!accordion}>
      <form>
        {!accordion && (
          <Typography variant="caption">{t("productPriceAndStock")}</Typography>
        )}
        <Styled.CardContainer>
          {edit ? (
            <>
              {fields.map((item, index) => {
                const data = watch("products");
                const priceRegister = register(`products.${index}.price`);
                return (
                  <Styled.Card key={item.id}>
                    <Styled.TitleView
                      colorText={data[index]?.enabled}
                      variant="body2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {formatByText.formatProductNameAndDescription(
                        data[index]?.name,
                        data[index]?.description,
                      )}
                      {isGasDoPovoProductId(
                        data[index]?.productId,
                        catalogProducts?.data,
                      ) && (
                        <Tooltip
                          title={t("gasDoPovoDeliveryFeeTooltip")}
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: () => ({
                                bgcolor: "#000000",
                                color: "#FFFFFF",
                                maxWidth: 320,
                              }),
                            },
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: "#000000",
                              }}
                              aria-label={String(
                                t("gasDoPovoDeliveryFeeTooltip"),
                              )}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Styled.TitleView>

                    <Styled.InputContainer>
                      <TextField
                        {...priceRegister}
                        name={`products.${index}.price`}
                        label={
                          isGasDoPovoProductId(
                            data[index]?.productId,
                            catalogProducts?.data,
                          )
                            ? t("delivery")
                            : t("price")
                        }
                        size="small"
                        onChange={(event) => {
                          onChangeMask.onChangeCurrencyMask(
                            event as React.ChangeEvent<HTMLInputElement>,
                          );
                          priceRegister.onChange(event);
                        }}
                      />
                      {/* TODO => switch do component */}
                      <Switch
                        defaultChecked={watch(`products.${index}.enabled`)}
                        size="small"
                        color="success"
                        {...register(`products.${index}.enabled`)}
                      />
                      <IconButton
                        aria-label={t("delete")}
                        onClick={() => remove(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Styled.InputContainer>
                  </Styled.Card>
                );
              })}
              {!accordion && (
                <Styled.Card>
                  <Styled.InputContainer>
                    <Autocomplete
                      {...registerAdd("productsAdd.productId")}
                      style={{ width: "180px" }}
                      disablePortal
                      value={{ id: productNewId, label: productSelected }}
                      id="combo-box-demo"
                      options={IProducts || []}
                      size="small"
                      noOptionsText="Sem opções"
                      isOptionEqualToValue={(option, newValue) => {
                        return option.id === newValue.id;
                      }}
                      onChange={(_, e) => {
                        setProductSelected((e?.label as any) || "");
                        setProductNewId((e?.id as any) || "");
                      }}
                      sx={{
                        width: 180,
                        "& input": { width: 180 },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          onBlur={(e) => setProductSelected(e.target.value)}
                          label={t("selectProduct")}
                        />
                      )}
                    />
                  </Styled.InputContainer>

                  <FormProvider {...methods}>
                    <Styled.InputContainer>
                      <TextField
                        {...registerAdd("productsAdd.price")}
                        name="productsAdd.price"
                        onChange={onChangeMask.onChangeCurrencyMask}
                        onBlur={(e) => setProductNewPrice(e.target.value)}
                        label={
                          isGasDoPovoProductId(
                            productNewId,
                            catalogProducts?.data,
                          )
                            ? t("delivery")
                            : t("price")
                        }
                        size="small"
                      />
                      {isGasDoPovoProductId(
                        productNewId,
                        catalogProducts?.data,
                      ) && (
                        <Tooltip
                          title={t("gasDoPovoDeliveryFeeTooltip")}
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: () => ({
                                bgcolor: "#000000",
                                color: "#FFFFFF",
                                maxWidth: 320,
                              }),
                            },
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: "#000000",
                              }}
                              aria-label={String(
                                t("gasDoPovoDeliveryFeeTooltip"),
                              )}
                            />
                          </span>
                        </Tooltip>
                      )}
                      <Styled.ButttonAdd
                        disabled={false}
                        onClick={() => {
                          const productValid = Boolean(productNewId);
                          const priceValid =
                            isValidProductPriceInput(productNewPrice);

                          if (!productValid) {
                            toast.error(String(t("selectProductBeforeAdd")));
                            return;
                          }

                          if (!priceValid) {
                            showZeroFieldToast();
                            return;
                          }

                          const catalogProduct = catalogProducts?.data?.find(
                            (p) => p.id === productNewId,
                          );
                          append({
                            description: catalogProduct?.description ?? "",
                            name: catalogProduct?.name ?? "",
                            enabled: true,
                            price: productNewPrice,
                            productId: productNewId,
                          });

                          setValue("productsAdd.productId", "");
                          setValue("productsAdd.price", "");
                          setProductSelected("");
                          setProductNewPrice("");
                          setProductNewId("");
                        }}
                        variant="text"
                        sx={{
                          padding: 1,
                          border: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {t("add").toUpperCase()}
                        </Typography>
                      </Styled.ButttonAdd>
                    </Styled.InputContainer>
                  </FormProvider>
                </Styled.Card>
              )}
            </>
          ) : (
            <Styled.CardContainer>
              {products?.products?.map((item) => {
                return (
                  <Styled.Card key={item.productId}>
                    <Styled.TitleView colorText={item.enabled} variant="body2">
                      {formatByText.formatProductNameAndDescription(
                        item.name,
                        item.description,
                      )}

                      {isGasDoPovoProductId(
                        item.productId,
                        catalogProducts?.data,
                      ) && (
                        <Tooltip
                          title={t("gasDoPovoDeliveryFeeTooltip")}
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: () => ({
                                bgcolor: "#000000",
                                color: "#FFFFFF",
                                maxWidth: 320,
                              }),
                            },
                          }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{
                                color: "#000000",
                              }}
                              aria-label={String(
                                t("gasDoPovoDeliveryFeeTooltip"),
                              )}
                            />
                          </span>
                        </Tooltip>
                      )}
                    </Styled.TitleView>

                    <Styled.TitleView colorText={item.enabled} variant="body2">
                      {item.price}
                    </Styled.TitleView>
                  </Styled.Card>
                );
              })}
            </Styled.CardContainer>
          )}
        </Styled.CardContainer>
        <Styled.Footer>
          {edit ? (
            <>
              <Button
                onClick={() => {
                  reset({ products: products.products });
                  resetFormFields();
                  setEdit(false);
                }}
                color="inherit"
                variant="outlined"
                endIcon={<CloseIcon />}
              >
                {t("close")}
              </Button>
              <Button
                onClick={() => handleSaveProducts()}
                color="error"
                variant="contained"
                endIcon={<SaveIcon />}
                type="button"
              >
                {t("save")}
              </Button>
            </>
          ) : (
            <Styled.DivContainerButton>
              {accordion && (
                <Button
                  onClick={() => handleOpenArea?.(dealerSelected)}
                  color="error"
                  variant="outlined"
                  sx={{
                    border: "1px solid #000000",
                    color: "#000000",
                    ":hover": {
                      border: "1px solid #000000",
                      background: "transparent",
                    },
                  }}
                >
                  {t("Abrir área")}
                </Button>
              )}
              <Button
                onClick={() => {
                  replace(products.products);
                  reset({ products: products.products });
                  resetFormFields();
                  setEdit(true);
                }}
                color="error"
                variant="contained"
              >
                {t("editPrices")}
              </Button>
            </Styled.DivContainerButton>
          )}
        </Styled.Footer>
      </form>
    </Styled.Container>
  );
};

export default CardProductCovered;
